import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, ChevronRight } from "lucide-react";
import HeaderDash from "../../components/HeaderDash";
import TripAvatar from "../../components/TripAvatar";
import bookingService from "../../api/services/bookingService";
import { useToast } from "../../hooks/useToast";
import { TRIP_TABS, groupTripsByTab } from "../../utils/myTrips";

const EMPTY_MESSAGES = {
  upcoming: "You have no upcoming trips.",
  in_progress: "You have no trip in progress.",
  past: "You have no past trips yet.",
};

const extractItems = (response) => {
  const items = response?.data?.items || response?.items || response?.data || [];
  return Array.isArray(items) ? items : [];
};

export default function MyTrips() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;

    bookingService
      .getMyBookings({ limit: 50 })
      .then((response) => {
        if (active) setBookings(extractItems(response));
      })
      .catch((err) => {
        if (!active) return;
        setLoadFailed(true);
        toast.apiError(err, "Unable to load your trips right now.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [toast]);

  const groups = useMemo(() => groupTripsByTab(bookings), [bookings]);

  // With no tab in the URL, open wherever the traveller has something to
  // confirm — that is usually why they came.
  const requestedTab = searchParams.get("tab");
  const activeTab = TRIP_TABS.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : TRIP_TABS.find((tab) => groups[tab.key].some((trip) => trip.needsAction))?.key ||
      "upcoming";
  const trips = groups[activeTab];

  return (
    <div className="min-h-screen bg-white">
      <HeaderDash />

      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="text-3xl font-bold text-[#61013B]">My Trips</h1>
        <p className="mt-1 text-sm font-medium text-gray-500">
          Your Pulo Assistant bookings, from request to the end of the trip.
        </p>

        <div
          role="tablist"
          aria-label="Trips"
          className="mt-8 flex gap-2 overflow-x-auto border-b border-gray-200"
        >
          {TRIP_TABS.map((tab) => {
            const selected = tab.key === activeTab;
            const hasAction = groups[tab.key].some((trip) => trip.needsAction);
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setSearchParams({ tab: tab.key }, { replace: true })}
                className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${
                  selected
                    ? "border-[#61013B] text-[#61013B]"
                    : "border-transparent text-gray-500 hover:text-[#61013B]"
                }`}
              >
                {tab.label}
                {!loading && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      hasAction ? "bg-[#FFF6E5] text-[#B26700]" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {groups[tab.key].length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {loading ? (
            <p className="rounded-2xl border border-gray-100 px-4 py-10 text-center text-sm font-medium text-gray-500">
              Loading your trips…
            </p>
          ) : loadFailed ? (
            <p className="rounded-2xl border border-gray-100 px-4 py-10 text-center text-sm font-medium text-gray-500">
              We couldn't load your trips. Please refresh to try again.
            </p>
          ) : trips.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center">
              <p className="text-sm font-medium text-gray-500">{EMPTY_MESSAGES[activeTab]}</p>
              {activeTab === "upcoming" && (
                <button
                  type="button"
                  onClick={() => navigate("/user/travel-assistant")}
                  className="mt-4 rounded-xl bg-[#61013B] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#4f0f32]"
                >
                  Find a Pulo Assistant
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-100">
              {trips.map((trip) => (
                <li key={trip.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/user/trips/${trip.id}`)}
                    className="flex w-full items-center gap-4 px-4 py-4 text-left transition hover:bg-[#faf6f9] sm:px-5"
                  >
                    <TripAvatar name={trip.assistantName} image={trip.assistantImage} />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate text-base font-semibold text-gray-900">
                        {trip.assistantName}
                      </p>
                      <p className="flex items-center gap-1.5 text-sm font-medium text-gray-500">
                        <CalendarDays className="h-4 w-4 shrink-0" />
                        {trip.dateRange}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#f2dfe9] px-2.5 py-0.5 text-xs font-semibold text-[#6b0c44]">
                          {trip.statusLabel}
                        </span>
                        {trip.actionLabel && (
                          <span className="rounded-full bg-[#FFF6E5] px-2.5 py-0.5 text-xs font-semibold text-[#B26700]">
                            {trip.actionLabel}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="hidden text-sm font-semibold text-gray-900 sm:inline">
                        {trip.total}
                      </span>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
